import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '../../components/button'
import meteorBlue from '../../assets/images/meteor-blue.svg'
import star from '../../assets/images/star.svg'
import Alert from '../../components/alert'
import { setShowIntroJs, setScore } from '../../redux/basic4/reducer'
import { withRouter } from 'react-router-dom'
import { CountdownCircleTimer } from 'react-countdown-circle-timer'
import { Steps } from 'intro.js-react'
import 'intro.js/introjs.css'

const operators = ['+', '-', 'x', '÷', '(', ')']
const correctAnswerScore = 10
const gameStatuses = {
  PLAYING: 'playing',
  CORRECT_ANSWER: 'correctAnswer',
  CLOSE_ANSWER: 'closeAnswer',
  WRONG_ANSWER: 'wrongAnswer',
  FAIL: 'fail',
  TIMEOUT: 'timeout',
  INVALID_INPUT: 'invalidInput'
}
const gameResultDefaults = {
  status: gameStatuses.PLAYING,
  starCount: 0,
  score: 0,
  answer: 0
}
const gameDurationDefault = 90
const multiKeyInputs = ['1', '2', '5', '7']
const multiKeySecondInputs = ['0', '5']

const _steps = [
  {
    element: '.timer',
    intro:
      'You have a limited time for each round and the remaining time is shown on the top right section of the window.',
    position: 'left'
  },
  {
    element: '.user-input-buttons',
    intro:
      'You can use the keyboard or mouse to write an arithmetic expression in the input box.',
    position: 'top'
  },
  {
    element: '#ClearUserInput',
    intro:
      'You can click on the <span class="custom-button primary"><i class="fas fa-times"></i></span> button to clear the entire input',
    position: 'left'
  },
  {
    element: '#RemoveLastUserInput',
    intro:
      'You can press "Backspace" or click on the <span class="custom-button primary"><i class="fas fa-arrow-left"></i></span> button to erase the last operator or the number in your input.',
    position: 'left'
  },
  {
    element: '#CheckResults',
    intro:
      'When you are done with the input, you can press "Enter" or click on the <span class="custom-button primary">Check Result</span> button',
    position: 'top'
  },
  {
    intro: (
      <>
        Stars:
        <br />
        If your score is 9 or 10, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <br />
        If your score is 6,7 or 8, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
        <img src={star} width='13' height='13' alt='star' className='ms-1' />
        <br />
        If your score is 1 through 5, you will see{' '}
        <img src={star} width='13' height='13' alt='star' />
      </>
    )
  }
]

const Single = props => {
  const dispatch = useDispatch()
  const inputRef = useRef()
  const { score, showIntroJs } = useSelector(state => state.basic4)
  const [userInput, setUserInput] = useState([])
  const [smallNumbers, setSmallNumbers] = useState([])
  const [bigNumbers, setBigNumbers] = useState([])
  const [targetNumber, setTargetNumber] = useState(null)
  const [gameResult, setGameResult] = useState(gameResultDefaults)
  const [gameDuration, setGameDuration] = useState(gameDurationDefault)
  const [timerKey, setTimerKey] = useState(0)
  const [multiKeyInput, setMultiKeyInput] = useState(null)
  const [introJsSteps, setIntroJsSteps] = useState(null)
  const [showRules, setShowRules] = useState(false)

  const handleToggleRules = () => {
    if (showRules) newGame()
    setShowRules(!showRules)
  }

  const handleShowIntroJs = () => {
    dispatch(setShowIntroJs(true))
  }

  const handleHideIntroJs = () => {
    dispatch(setShowIntroJs(false))
    setIntroJsSteps(null)
    newGame()
  }

  const handleExampleUserInputs = () => {
    setUserInput([
      {
        type: 'small',
        input: smallNumbers[0],
        index: 0
      },
      {
        type: 'operator',
        input: 'x',
        index: 0
      },
      {
        type: 'small',
        input: smallNumbers[1],
        index: 1
      }
    ])
  }

  useEffect(() => {
    if (showIntroJs && smallNumbers.length > 0 && userInput.length === 0) {
      handleExampleUserInputs()
      setIntroJsSteps(_steps)
    }
    // eslint-disable-next-line
  }, [showIntroJs, smallNumbers, userInput])

  const generateSmallNumbers = () => {
    let numbers = []
    for (let i = 0; i < 4; i++) numbers.push(Math.floor(Math.random() * 9) + 1)
    numbers.sort(function (a, b) {
      return a - b
    })
    setSmallNumbers(numbers)
  }

  const generateBigNumbers = () => {
    const _bigNumbers = [25, 50, 75, 100]
    let numbers = []
    for (let i = 0; i < 2; i++)
      numbers.push(
        _bigNumbers.splice(Math.floor(Math.random() * _bigNumbers.length), 1)[0]
      )
    numbers.sort(function (a, b) {
      return a - b
    })
    setBigNumbers(numbers)
  }

  const generateTargetNumber = () => {
    setTargetNumber(Math.floor(Math.random() * 898) + 101)
  }

  const handleCheckUserInputOrder = (input, type) => {
    if (userInput.length === 0 && type === 'operator' && input !== '(')
      return false
    else if (userInput.length > 0) {
      const lastInput = userInput[userInput.length - 1]
      if (
        (lastInput.type === 'small' || lastInput.type === 'big') &&
        (type === 'small' || type === 'big')
      )
        return false
      else if (
        lastInput.type === 'operator' &&
        type === 'operator' &&
        lastInput.input !== ')' &&
        lastInput.input !== '(' &&
        input !== '('
      )
        return false
      else if (lastInput.input === ')' && (type === 'small' || type === 'big'))
        return false
      else if (lastInput.input === '(' && type === 'operator' && input !== '(')
        return false
      else if (lastInput.input === ')' && input === '(') return false
    }
    return true
  }

  const handleSetUserInput = (input, type, index) => event => {
    if (handleCheckUserInputOrder(input, type)) {
      setUserInput([
        ...userInput,
        {
          type,
          input,
          index: index || 0
        }
      ])
    }
    event.currentTarget.blur()
  }

  const newGame = () => {
    if (
      //gameResult.status === gameStatuses.CORRECT_ANSWER ||
      gameResult.status === gameStatuses.CLOSE_ANSWER
    ) {
      dispatch(setScore(score + gameResult.score))
    }
    resetGame()
  }

  const continueToPlay = () => {
    if (
      gameResult.status === gameStatuses.CLOSE_ANSWER ||
      gameResult.status === gameStatuses.WRONG_ANSWER ||
      gameResult.status === gameStatuses.INVALID_INPUT
    ) {
      setGameResult({
        ...gameResult,
        status: gameStatuses.PLAYING
      })
    }
  }

  const resetGame = () => {
    setGameResult(gameResultDefaults)
    setUserInput([])
    generateSmallNumbers()
    generateBigNumbers()
    generateTargetNumber()
    setGameDuration(gameDurationDefault)
    setTimerKey(timerKey + 1)
  }

  const handleClearUserInput = () => {
    setUserInput([])
  }

  const handleRemoveLastUserInput = () => {
    if (userInput.length > 0) {
      let _userInput = [...userInput]
      _userInput.pop()
      setUserInput(_userInput)
    }
  }

  const validateUserInputs = () => {
    let valid = true
    userInput.forEach(input => {
      if (
        smallNumbers.indexOf(input.input) === -1 &&
        bigNumbers.indexOf(input.input) === -1 &&
        operators.indexOf(input.input) === -1
      )
        valid = false
    })
    return valid
  }

  const handleCheckUserInput = () => {
    if (validateUserInputs()) {
      let userInputText = userInput.map(input => input.input).join('')
      userInputText = userInputText.replace(/x/g, '*')
      userInputText = userInputText.replace(/÷/g, '/')
      try {
        // eslint-disable-next-line
        const userResult = eval(userInputText)

        if (userResult === targetNumber) {
          setGameResult({
            status: gameStatuses.CORRECT_ANSWER,
            score: 10,
            starCount: 3
          })
          dispatch(setScore(score + 10))
        } else {
          const diff = Math.round(Math.abs(targetNumber - userResult))
          let score = correctAnswerScore - diff
          if (score <= 0) {
            setGameResult({
              status: gameStatuses.WRONG_ANSWER,
              score: 0,
              starCount: 0,
              answer: userResult
            })
          } else {
            const starCount = score > 8 ? 3 : score > 5 ? 2 : 1
            setGameResult({
              status: gameStatuses.CLOSE_ANSWER,
              score,
              starCount,
              answer: userResult
            })
          }
        }
      } catch {
        setGameResult({
          status: gameStatuses.INVALID_INPUT
        })
      }
    } else {
      setGameResult({
        status: gameStatuses.INVALID_INPUT
      })
    }
  }

  const handleButtonDisabled = (number, type, index) => {
    const isExists = userInput.find(
      input =>
        input.input === number && input.type === type && input.index === index
    )
    if (isExists) return true
    return false
  }

  const handleTimerIsPlaying = () => {
    if (
      gameResult.status === gameStatuses.CORRECT_ANSWER ||
      gameResult.status === gameStatuses.TIMEOUT ||
      showIntroJs ||
      showRules
    )
      return false
    return true
  }

  const handleTimeout = () => {
    setGameResult(prevState => {
      if (prevState.score > 0) dispatch(setScore(score + prevState.score))
      return {
        ...prevState,
        status: gameStatuses.TIMEOUT
      }
    })
    return [true, 250]
  }

  const validateKeyPress = event => {
    if (
      smallNumbers.indexOf(parseInt(event.key)) === -1 &&
      multiKeyInputs.indexOf(event.key) === -1 &&
      operators.indexOf(event.key) === -1
    )
      return false
    return true
  }

  const isPossibleBigNumber = key => {
    if (multiKeyInputs.indexOf(key) !== -1) {
      const bigNumber = bigNumbers.find(
        number => String(number).charAt(0) === key
      )
      if (
        bigNumber &&
        !userInput.find(
          input => input.type === 'big' && input.input === bigNumber
        )
      )
        return true
    }
    return false
  }

  const isSingleKeyInput = event => {
    const smallNumberCount = smallNumbers.filter(
      number => number === parseInt(event.key)
    ).length
    const userInputCount = userInput.filter(
      input => input.type === 'small' && input.input === parseInt(event.key)
    ).length

    if (smallNumberCount > userInputCount && !isPossibleBigNumber(event.key))
      return true
    return false
  }

  const isMultiKeyInput = event => {
    if (multiKeyInputs.indexOf(event.key) !== -1) return true
    return false
  }

  const findIndexInSmall = key => {
    for (let i = 0; i < smallNumbers.length; i++) {
      if (
        parseInt(key) === smallNumbers[i] &&
        !userInput.find(
          input =>
            input.type === 'small' &&
            input.input === smallNumbers[i] &&
            input.index === i
        )
      ) {
        return i
      }
    }
    return -1
  }

  const findIndexInBig = key => {
    for (let i = 0; i < bigNumbers.length; i++) {
      if (
        parseInt(key) === bigNumbers[i] &&
        !userInput.find(
          input =>
            input.type === 'big' &&
            input.input === bigNumbers[i] &&
            input.index === i
        )
      ) {
        return i
      }
    }
    return -1
  }

  const createInputType = event => {
    const indexInSmall = findIndexInSmall(event.key)
    const indexInBig = findIndexInBig(event.key)
    const indexInMulti = multiKeyInputs.indexOf(event.key)
    const indexInOperator = operators.indexOf(event.key)
    if (indexInSmall !== -1) {
      return {
        type: 'small',
        input: parseInt(event.key),
        index: indexInSmall
      }
    } else if (indexInBig !== -1)
      return {
        type: 'big',
        input: parseInt(event.key),
        index: indexInBig
      }
    else if (indexInMulti !== -1)
      return {
        type: 'big',
        input: parseInt(event.key),
        index: indexInMulti
      }
    else if (event.key === '10') {
      if (!bigNumbers.find(number => number === 100)) return false
      else if (
        userInput.find(input => input.type === 'big' && input.input === 100)
      )
        return false
      return {
        type: 'big',
        input: 100,
        index: bigNumbers.findIndex(number => number === 100)
      }
    } else if (indexInOperator !== -1)
      return {
        type: 'operator',
        input: event.key,
        index: indexInOperator
      }
    return false
  }

  const handleWindowKeyDown = event => {
    event.preventDefault()

    let myEvent = { key: event.key, keyCode: event.keyCode }
    if (myEvent.key === '*') myEvent.key = 'x'
    if (myEvent.key === '/') myEvent.key = '÷'

    if (myEvent.keyCode === 27) handleClearUserInput()
    else if (myEvent.keyCode === 13 && userInput.length > 2) {
      if (gameResult.status === gameStatuses.PLAYING) handleCheckUserInput()
      else if (
        gameResult.status === gameStatuses.CORRECT_ANSWER ||
        gameResult.status === gameStatuses.FAIL ||
        gameResult.status === gameStatuses.TIMEOUT
      )
        newGame()
      else if (
        gameResult.status === gameStatuses.CLOSE_ANSWER ||
        gameResult.status === gameStatuses.WRONG_ANSWER ||
        gameResult.status === gameStatuses.INVALID_INPUT
      )
        continueToPlay()
    } else if (myEvent.keyCode === 8) {
      handleRemoveLastUserInput()
      if (multiKeyInput !== null) setMultiKeyInput(null)
    } else if (
      multiKeyInput !== null &&
      multiKeySecondInputs.indexOf(myEvent.key) !== -1
    ) {
      myEvent.key = multiKeyInput + myEvent.key
      const inputType = createInputType(myEvent)
      if (
        inputType !== false &&
        handleCheckUserInputOrder(myEvent.key, inputType.type)
      ) {
        setUserInput([...userInput, inputType])
        setMultiKeyInput(null)
      } else if (inputType === false) {
        setMultiKeyInput(null)
      } else {
        setMultiKeyInput(myEvent.key)
      }
    } else if (multiKeyInput !== null) {
      const inputType = createInputType(myEvent)
      const multiInputType = createInputType({ key: multiKeyInput })
      if (
        inputType !== false &&
        multiInputType !== false &&
        inputType.type === 'operator' &&
        isSingleKeyInput({ key: multiInputType.input })
      ) {
        setUserInput([...userInput, multiInputType, inputType])
        setMultiKeyInput(null)
      } else if (inputType !== false && isSingleKeyInput(myEvent)) {
        setUserInput([...userInput, inputType])
        setMultiKeyInput(null)
      }
    } else if (validateKeyPress(myEvent)) {
      const inputType = createInputType(myEvent)
      if (
        inputType !== false &&
        handleCheckUserInputOrder(myEvent.key, inputType.type)
      ) {
        if (isSingleKeyInput(myEvent)) {
          setUserInput([...userInput, inputType])
        } else if (isMultiKeyInput(myEvent)) {
          setMultiKeyInput(myEvent.key)
        } else if (inputType.type === 'operator') {
          setUserInput([...userInput, inputType])
        }
      }
    }
  }

  useEffect(() => {
    resetGame()
    return () => {}
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', handleWindowKeyDown)
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown)
    }
    // eslint-disable-next-line
  }, [userInput, multiKeyInput, gameResult])

  return (
    <div className='game-play d-flex justify-content-center align-items-center align-self-center text-center h-100'>
      <div className='header d-flex justify-content-between w-100'>
        <div style={{ width: 180, textAlign: 'left', display: 'flex' }}>
          <div className='text-bg user-score' title='Score'>
            <img src={star} width='24' height='24' alt='star' />
            {score}
          </div>
        </div>
        <div className='title d-flex justify-content-center'>
          <img src={meteorBlue} width='36' height='36' alt='meteor' />
          <h1 className='title'>Basic 4</h1>
        </div>
        <div style={{ width: 180 }}>
          {/* <Button to='/' className='me-2' title='Home Page'>
            <i className='fas fa-home'></i>
          </Button> */}
          <Button
            color='primary'
            className='me-2'
            title='Show Game Info'
            onClick={handleToggleRules}
          >
            <i className='fas fa-info'></i>
          </Button>
          <Button
            onClick={handleShowIntroJs}
            className='me-2'
            title='How to Play'
          >
            <i className='fas fa-question'></i>
          </Button>
          <Button to='/basic4' title='Close Game'>
            <i className='fas fa-times'></i>
          </Button>
        </div>
      </div>

      <div className='play-area' ref={inputRef}>
        <h3>Target Number</h3>
        <div className='d-inline-block text-bg'>{targetNumber}</div>

        <div className='timer'>
          <CountdownCircleTimer
            isPlaying={handleTimerIsPlaying()}
            duration={gameDuration}
            size={64}
            strokeWidth={7}
            colors={[
              //['#00700e', 0.33],
              ['#FFB500', 0.33],
              ['#FF5200', 0.33]
            ]}
            onComplete={handleTimeout}
            key={timerKey}
          >
            {({ remainingTime }) => remainingTime}
          </CountdownCircleTimer>
        </div>

        <div className='text-bg-2'>
          <span className='user-input'>
            {userInput.map(input => input.input)}
          </span>
          {userInput.length > 0 && (
            <div className='buttons'>
              <Button
                onClick={handleRemoveLastUserInput}
                className='remove-last-user-input'
                id='RemoveLastUserInput'
              >
                <i className='fas fa-arrow-right'></i>
              </Button>
              <Button onClick={handleClearUserInput} id='ClearUserInput'>
                <i className='fas fa-times'></i>
              </Button>
            </div>
          )}
        </div>

        <div className='user-input-buttons'>
          <div className='d-flex w-100 mt-4 justify-content-center'>
            {smallNumbers.map((number, index) => (
              <Button
                onClick={handleSetUserInput(number, 'small', index)}
                key={index}
                disabled={handleButtonDisabled(number, 'small', index)}
                className={
                  parseInt(multiKeyInput) === number ? 'possiblePress' : ''
                }
              >
                {number}
              </Button>
            ))}
            {bigNumbers.map((number, index) => (
              <Button
                onClick={handleSetUserInput(number, 'big', index)}
                key={index}
                disabled={handleButtonDisabled(number, 'big', index)}
                className={
                  multiKeyInput === String(number).charAt(0)
                    ? 'possiblePress'
                    : ''
                }
              >
                {number}
              </Button>
            ))}
          </div>

          <div className='d-flex w-100 justify-content-center'>
            {operators.map((operator, index) => {
              return (
                <Button
                  onClick={handleSetUserInput(operator, 'operator')}
                  color='secondary'
                  key={index}
                >
                  {operator}
                </Button>
              )
            })}
          </div>
        </div>

        {userInput.length > 2 && (
          <div className='d-flex w-100 justify-content-center mt-1'>
            <Button onClick={handleCheckUserInput} id='CheckResults'>
              Check Result
            </Button>
          </div>
        )}

        <Alert
          show={gameResult.status === gameStatuses.CORRECT_ANSWER}
          text={'Congratulations!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.CLOSE_ANSWER}
          text={'You are so close!'}
          subText={'Your result: ' + gameResult.answer}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'Continue to Try!',
              onClick: continueToPlay,
              color: 'primary'
            },
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.WRONG_ANSWER}
          text={'Oops...too far from the target!'}
          subText={'Your result: ' + gameResult.answer}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'Continue to Try!',
              onClick: continueToPlay,
              color: 'primary'
            },
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.FAIL}
          text={'Oops...you are failed!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.TIMEOUT}
          text={'Oops...out of time!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'New Game!',
              onClick: newGame,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.INVALID_INPUT}
          text={'Oops...check your operations!'}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: 'Continue',
              onClick: continueToPlay,
              color: 'primary'
            }
          ]}
        />
        <Alert
          show={showRules}
          html={
            <div style={{ maxWidth: 500 }}>
              <h5>Objective</h5>
              <p>
                Your goal is to reach or get close to the given "target number"
                using four basic operations and given numbers.
              </p>
              <h5>Rules</h5>
              <p>
                You are given limited time.
                <br />
                You can use each number at most once
                <br />
                You can only use the given operations +, -, x, /, and
                parentheses
                <br />
              </p>
              <h5>Scoring </h5>
              <p>
                10 pts to reach the target number
                <br />
                You will get points if you are within 9 away from the target
                number; 9 points if you are 1 away, 8 points if you are 2 away,
                etc.
              </p>
              <p>
                If your first attempt is not the target number and you still
                have time, you can try again until the time is up. You will be
                scored based on your last attempt.
                <br />
              </p>
            </div>
          }
          buttons={[
            {
              text: 'Close',
              onClick: handleToggleRules,
              color: 'primary'
            }
          ]}
        />
      </div>

      {showIntroJs && introJsSteps !== null && (
        <Steps
          enabled={showIntroJs}
          steps={introJsSteps}
          initialStep={0}
          onExit={handleHideIntroJs}
          options={{
            hidePrev: true,
            hideNext: true,
            doneLabel: 'Done',
            nextToDone: true
          }}
        />
      )}
    </div>
  )
}

export default withRouter(Single)
